import { FC, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'src/components/FormInputs/i18n';
import {
  IconButton,
  Tooltip,
  Input,
  InputProps,
  TooltipProps,
} from 'material-hu';
import Compressor from 'compressorjs';
import { MAX_ATTACHED } from 'src/constants/attachments';
import { FormFile as FormFileType, FileTypes } from 'src/types/attachments';
import { getDimensions } from 'src/services/attachments';
import './i18n';

export const NEW_WIDTH = 1000;
export const NEW_HEIGHT = 1000;

type Props = InputProps &
  Pick<TooltipProps, 'placement'> & {
    title: string;
    type: string;
    icon: any;
    size?: any;
    iconSx?: any;
    onChange?: (file: any) => void;
    disabled?: boolean;
    iconColor?: string;
    inputProps?: any;
    multiple?: boolean;
    multimediaSize?: number;
    filesSize?: number;
    isFileMultiple?: boolean;
    maxMultimedia?: number;
    maxFiles?: number;
    checkMax?: boolean;
  };

const FormFile: FC<Props> = props => {
  const {
    name,
    title,
    type,
    icon,
    size = 'large',
    disabled = false,
    placement = 'bottom-end',
    inputProps,
    onChange,
    iconColor = 'primary.main',
    multiple = false,
    multimediaSize = 0,
    filesSize = 0,
    isFileMultiple = false,
    maxMultimedia = MAX_ATTACHED.MULTIMEDIA,
    maxFiles = MAX_ATTACHED.FILES,
    checkMax = true,
    ...others
  } = props;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleAttach = (): void => {
    fileInputRef.current.click();
  };

  const checkMultipleFilesUpload = files => {
    return (
      (files.length <= maxMultimedia - multimediaSize && !isFileMultiple) ||
      (files.length <= maxFiles - filesSize && isFileMultiple)
    );
  };

  const handleChange = async event => {
    const inputFile = event.target.files[0];

    if (type === FileTypes.IMAGE) {
      try {
        await new Compressor(inputFile, {
          quality: 0.5,
          convertTypes: ['image/png', 'image/webp'],
          convertSize: 500000,
          maxHeight: NEW_HEIGHT,
          maxWidth: NEW_WIDTH,
          success: async (compressedResult: File) => {
            const file = new File([compressedResult], compressedResult.name, {
              type: compressedResult.type,
            });
            try {
              const dimensions = await getDimensions(file);
              inputFile.width = dimensions.width;
              inputFile.height = dimensions.height;
            } catch (err) {
              inputFile.width = undefined;
              inputFile.height = undefined;
            }
            if (file) {
              const newFile: FormFileType = {
                name: file?.name,
                size: file?.size,
                mime: file?.type,
                width: inputFile?.width,
                height: inputFile?.height,
                fileObject: file,
                type,
                toUpload: true,
              };

              onChange(newFile);
            }
          },
        });
      } catch (err) {
        enqueueSnackbar(t('COMPRESS_IMAGE_ERROR'), { variant: 'error' });
      }
    } else if (inputFile) {
      const newFile: FormFileType = {
        name: inputFile?.name,
        size: inputFile?.size,
        mime: inputFile?.type,
        width: inputFile?.width,
        height: inputFile?.height,
        fileObject: inputFile,
        type,
        toUpload: true,
      };

      onChange(newFile);
    }
  };

  const handleChangeMultiple = async event => {
    const { files } = event.target;
    const filePromises = [];

    if (!checkMax || checkMultipleFilesUpload(files)) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.includes('image') && !isFileMultiple) {
          filePromises.push(compressImage(files[i]));
        } else if (files[i].type.includes('video') && !isFileMultiple) {
          filePromises.push(addVideo(files[i]));
        } else {
          filePromises.push(addFile(files[i]));
        }
      }

      Promise.all(filePromises).then(compressedFiles => {
        onChange(compressedFiles);
      });
    } else {
      enqueueSnackbar(
        isFileMultiple
          ? t('UPLOAD_LIMIT_FILES', { count: maxFiles })
          : t('UPLOAD_LIMIT_MULTIMEDIA', { count: maxMultimedia }),
        { variant: 'warning' },
      );
    }
  };

  const addFile = inputFile =>
    new Promise(resolve => {
      const newFile: FormFileType = {
        name: inputFile?.name,
        size: inputFile?.size,
        mime: inputFile?.type,
        width: inputFile?.width,
        height: inputFile?.height,
        fileObject: inputFile,
        type,
        toUpload: true,
      };
      resolve(newFile);
    });

  const addVideo = inputFile =>
    new Promise(resolve => {
      if (inputFile?.name.endsWith('.mov')) {
        enqueueSnackbar(t('NOT_SUPPORT_MOV_EXTENSION'), { variant: 'warning' });
        return;
      }

      const reader = new FileReader();
      reader.onload = event => {
        const videoBlob = new Blob([event.target.result], {
          type: inputFile.type,
        });
        const tempVideo = document.createElement('video');
        tempVideo.src = URL.createObjectURL(videoBlob);

        tempVideo.onloadedmetadata = () => {
          const videoWidth = tempVideo.videoWidth;
          const videoHeight = tempVideo.videoHeight;
          const newFile: FormFileType = {
            name: inputFile?.name,
            size: inputFile?.size,
            mime: inputFile?.type,
            width: videoWidth,
            height: videoHeight,
            fileObject: inputFile,
            type,
            toUpload: true,
          };
          resolve(newFile);
        };
      };
      reader.readAsArrayBuffer(inputFile);
    });

  const compressImage = inputFile =>
    new Promise(resolve => {
      try {
        // eslint-disable-next-line no-new
        new Compressor(inputFile, {
          quality: 0.8,
          success: async compressedResult => {
            const file = new File([compressedResult], inputFile.name);
            try {
              const dimensions = await getDimensions(file);
              inputFile.width = dimensions.width;
              inputFile.height = dimensions.height;
            } catch (err) {
              inputFile.width = undefined;
              inputFile.height = undefined;
            }
            if (file) {
              const newFile: FormFileType = {
                name: file?.name,
                size: file?.size,
                mime: inputFile?.type,
                width: inputFile?.width,
                height: inputFile?.height,
                fileObject: file,
                type: FileTypes.IMAGE,
                toUpload: true,
              };

              resolve(newFile);
            }
          },
        });
      } catch (err) {
        enqueueSnackbar(t('COMPRESS_IMAGE_ERROR'), { variant: 'error' });
      }
    });

  const inputSx = others.sx || {};

  const iconSx = others.iconSx || {};

  return (
    <>
      <Tooltip
        title={title}
        placement={placement}
      >
        <IconButton
          edge="end"
          onClick={handleAttach}
          disabled={disabled}
          sx={{
            color: iconColor,
            px: 2,
            '& .MuiSvgIcon-root': {
              fontSize: size === 'small' ? 22 : 24,
            },
            ...iconSx,
          }}
          size={size}
        >
          {icon}
        </IconButton>
      </Tooltip>
      <Input
        {...others}
        type="file"
        sx={{
          display: 'none',
          ...inputSx,
        }}
        disabled={disabled}
        inputRef={fileInputRef}
        onChange={multiple ? handleChangeMultiple : handleChange}
        inputProps={inputProps}
      />
    </>
  );
};

export default FormFile;
